import React, { useRef, useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import './SuspendDatePicker.css';
import { ChevronDown, ChevronUp } from 'lucide-react';
import SubmitButton from '@/ctComponents/submitButton';
import CloseButton from '@/ctComponents/closeButton';

const formatDateForInput = (date) => {
  if (!date) return '';
  const month = date.toLocaleString('en-US', { month: 'short' });
  const dayName = date.toLocaleString('en-US', { weekday: 'short' });
  return `${month}, ${dayName} ${date.getDate()}`;
};

const parseDateFromString = (dateString) => {
  if (!dateString) return null;
  // Try parsing ISO format first (e.g., "2026-02-12T00:00:00.000000Z")
  if (dateString.includes('T')) {
    const date = new Date(dateString);
    if (!isNaN(date.getTime())) {
      return date;
    }
  }
  // Parse "YYYY-MM-DD HH:mm" format
  const [datePart, timePart] = dateString.split(' ');
  if (!datePart) return null;
  const [year, month, day] = datePart.split('-').map(Number);
  let hours = 0;
  let minutes = 0;
  if (timePart) {
    const [h, m] = timePart.split(':').map(Number);
    hours = h || 0;
    minutes = m || 0;
  }
  return new Date(year, month - 1, day, hours, minutes);
};

export const useSuspendDatePicker = ({
  item,
  isSuspended,
  loading,
  onSuspend,
  onUnsuspend,
  onClose,
  buttonRef,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [tempStartDate, setTempStartDate] = useState(null);
  const [tempEndDate, setTempEndDate] = useState(null);
  const [buttonPosition, setButtonPosition] = useState(null);
  const popoverRef = useRef(null);

  const updateButtonPosition = useCallback(() => {
    if (!buttonRef?.current) return;

    requestAnimationFrame(() => {
      const rect = buttonRef.current.getBoundingClientRect();
      const viewportWidth = window.innerWidth;

      const spacing = 8;

      const shouldOpenAbove = window.innerHeight - rect.bottom < 400;

      const top = shouldOpenAbove ? 'unset' : rect.bottom + spacing;
      const bottom = shouldOpenAbove
        ? window.innerHeight - rect.bottom + 24
        : 'unset';

      const right = viewportWidth - rect.right;

      setButtonPosition({
        top,
        right,
        positionAbove: shouldOpenAbove,
        bottom,
      });
    });
  }, [buttonRef]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isOpen) {
        if (
          popoverRef.current &&
          !popoverRef.current.contains(event.target) &&
          buttonRef?.current &&
          !buttonRef.current.contains(event.target)
        ) {
          setIsOpen(false);
          if (onClose) {
            onClose();
          }
        }
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, buttonRef, onClose]);

  // Update button position when opened or on scroll/resize
  useEffect(() => {
    if (!isOpen) return;

    updateButtonPosition();

    const handleUpdate = () => updateButtonPosition();
    window.addEventListener('scroll', handleUpdate, {
      passive: true,
      capture: true,
    });
    window.addEventListener('resize', handleUpdate, { passive: true });

    return () => {
      window.removeEventListener('scroll', handleUpdate, { capture: true });
      window.removeEventListener('resize', handleUpdate);
    };
  }, [isOpen, updateButtonPosition]);

  const handleDateRangeChange = useCallback((dates) => {
    const [start, end] = dates;
    setTempStartDate(start);
    setTempEndDate(end);
  }, []);

  const handleOpen = useCallback(() => {
    // Set dates synchronously before opening to ensure they're available when DatePicker renders
    if (isSuspended && item) {
      // Parse existing suspend dates from suspend_start_at and suspend_end_at
      const suspendStart = parseDateFromString(item.suspend_start_at);
      const suspendEnd = parseDateFromString(item.suspend_end_at);
      setTempStartDate(suspendStart);
      setTempEndDate(suspendEnd);
    } else {
      setTempStartDate(null);
      setTempEndDate(null);
    }
    setIsOpen(true);
  }, [isSuspended, item]);

  const handleSuspendClick = useCallback(() => {
    if (!tempStartDate || !tempEndDate) {
      return;
    }
    setIsOpen(false);
    if (onSuspend) {
      onSuspend(item, tempStartDate, tempEndDate);
    }
  }, [item, tempStartDate, tempEndDate, onSuspend]);

  const handleUnsuspendClick = useCallback(() => {
    setIsOpen(false);
    if (onUnsuspend) {
      onUnsuspend(item);
    }
  }, [item, onUnsuspend]);

  const handleClose = () => setIsOpen(false);

  const renderPopover = () => {
    if (!isOpen || !buttonPosition) return null;

    const popoverContent = (
      <div
        ref={popoverRef}
        className="fixed bg-white border border-solid border-light-gray-border rounded shadow-lg w-80 md:w-291 box-border p-4"
        style={{
          top: `${buttonPosition.top}px`,
          right: `${buttonPosition.right}px`,
          bottom: `${buttonPosition.bottom}px`,
          zIndex: 999999999,
          maxHeight: '90vh',
          overflowY: 'auto',
        }}>
        <div className="flex flex-col">
          <div className="custom-suspend-datepicker-wrapper">
            <DatePicker
              selected={tempStartDate}
              onChange={handleDateRangeChange}
              startDate={tempStartDate}
              endDate={tempEndDate}
              selectsRange
              inline
              showPopperArrow={false}
            />
          </div>

          <div className="flex flex-col items-center gap-2 pt-2 justify-between border-solid border-b-0 border-l-0 border-r-0 border-t border-border-tertiary">
            {tempStartDate && tempEndDate && (
              <div className="flex items-center gap-2 relative box-border bg-border-light-secondary p-2 w-full justify-between">
                {tempStartDate && (
                  <div className="flex items-center gap-1">
                    <span className="text-11 text-primary-text">From:</span>
                    <div className="bg-white border border-border-tertiary border-solid rounded h-8 px-4 flex items-center">
                      <p className=" text-primary-text whitespace-nowrap text-11">
                        {formatDateForInput(tempStartDate)}
                      </p>
                    </div>
                  </div>
                )}

                {tempEndDate && (
                  <div className="flex items-center gap-1">
                    <span className="text-11 text-primary-text">To:</span>
                    <div className="bg-white border border-border-tertiary border-solid rounded h-8 px-4 flex items-center justify-center">
                      <p className="text-11  text-primary-text whitespace-nowrap">
                        {formatDateForInput(tempEndDate)}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            )}
            <div className="flex gap-2 justify-between items-center w-full">
              {isSuspended ? (
                <CloseButton
                  onClick={handleSuspendClick}
                  text="Save Changes"
                  className="h-8 min-w-unset"
                  width={120}
                  disabled={loading || !tempStartDate || !tempEndDate}
                />
              ) : (
                <CloseButton
                  onClick={handleClose}
                  text="Close"
                  className="h-8 min-w-unset"
                  width={120}
                  disabled={loading || !tempStartDate || !tempEndDate}
                />
              )}
              {isSuspended ? (
                <SubmitButton
                  onClick={handleUnsuspendClick}
                  text="Unsuspend"
                  className="h-8 min-w-unset"
                  width={100}
                  disabled={loading}
                />
              ) : (
                <SubmitButton
                  onClick={handleSuspendClick}
                  text="Suspend"
                  className="h-8 min-w-unset"
                  width={120}
                  disabled={loading || !tempStartDate || !tempEndDate}
                />
              )}
            </div>
          </div>
        </div>
      </div>
    );

    return createPortal(popoverContent, document.body);
  };

  return {
    handleOpen,
    renderPopover,
    isOpen,
  };
};

export { parseDateFromString };

export const SuspendDatePickerButton = ({
  item,
  isSuspended,
  loading,
  onSuspend,
  onUnsuspend,
  onClose,
}) => {
  const buttonRef = useRef(null);
  const { handleOpen, renderPopover, isOpen } = useSuspendDatePicker({
    item,
    isSuspended,
    loading,
    onSuspend,
    onUnsuspend,
    onClose,
    buttonRef,
  });

  const isDisabled = loading || item.warning_type === 'NORMAL';

  return (
    <>
      <button
        ref={buttonRef}
        onClick={handleOpen}
        disabled={isDisabled}
        className={`
          ${
            isDisabled
              ? 'bg-border-light-secondary text-light-gray-border'
              : 'hover:shadow-border-button  bg-white text-warning-suspend'
          }
          px-3 h-6 min-w-102 shadow-none border-solid text-xs border border-border-tertiary font-bold rounded  font-averta cursor-pointer  transition-colors flex items-center justify-center gap-1`}>
        {isSuspended ? 'Unsuspend' : 'Suspend'}
        {isOpen ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
      </button>
      {renderPopover()}
    </>
  );
};
